// IngestHost — the source-ingest flow's impure shell (strangler-fig P1).
//
// Moved out of AppShellHost verbatim (.claude/analysis/ui-ddd-decomposition.md
// §6 Phase 1): owns the hidden file-picker input, the full-window drag-drop
// listeners (owner decision B5 — dropping files ANYWHERE adds them as
// sources to the workspace), and the running batch's per-file progress,
// folded through the pure `app/ingest.ts` vocabulary. Only OS file drags arm
// the drop overlay (`isFileDrag`); in-page drags pass untouched.
//
// The picker is triggered from many places outside this component's own
// render tree (the rail's foot "+", the File menu, WorkspaceHome's next
// step, the desktop menu accelerators) — all owned by AppShellHost, not by
// this host.
// Since the `<input>` DOM node must stay inside IngestHost (it owns
// `fileInputRef`), `open()` is exposed via an imperative handle rather than
// lifting the ref itself back up to the shell.
//
// `ingest` (the in-flight batch) also feeds the activity rail's quiet
// "Adding N sources…" status line (`ActivityRail`'s Sources panel) — so this
// host reports every change via `onIngestChanged`, and the shell mirrors it
// into its own read-only copy for that one render site.

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import type { ExternalError } from "../../app/externalError.js";
import { toDisplay } from "../../app/externalError.js";
import type { HostNotice } from "../../app/frame.js";
import {
    ACCEPTED_SOURCE_EXTENSIONS,
    type IngestFile,
    type IngestId,
    isFileDrag,
} from "../../app/ingest.js";
import {
    decideIngest,
    evolveIngestAll,
    type IngestCommand,
    type IngestEffect,
    initialIngestState,
    type QueuedFile,
} from "../../app/ingestDecider.js";
import type { AddedSource, AppPorts, Library } from "../../app/ports.js";
import { SourceDropOverlay } from "../../app/SourceDropOverlay.js";
import type { TriggerProvenance } from "../../app/trigger.js";
import type { ResourceRef } from "../Navigation/types.js";

export type IngestHostHandle = {
    // Opens the hidden file picker — the one entry point every "add source"
    // affordance (rail, menu, WorkspaceHome, desktop accelerators) calls.
    readonly open: () => void;
    // The ingest decider bridge, reached back through this handle by the shell's
    // one stamped door (determinism M9.2): a routed `ingest` trigger lands here
    // and runs decide→evolve→interpret against IngestHost's own batch state.
    // The batch state lives in this host, so the bridge must too — the shell
    // reaches it via the SAME imperative handle it already uses for `open()`.
    readonly applyCommand: (command: IngestCommand) => void;
};

export type IngestHostProps = {
    readonly ports: AppPorts;
    // Where the dropped files will land — rendered by the drop overlay.
    readonly workspaceName: string;
    // The library reload after a batch lands (the rail's new rows).
    readonly onLibraryChanged: (library: Library) => void;
    // Opens the last-added source as a tab, ONE per batch (§ ingestFiles).
    readonly openRef: (ref: ResourceRef) => void;
    readonly showNotice: (notice: HostNotice) => void;
    // Mirrors the in-flight batch up so the shell's rail status line
    // (`pendingCount`) can keep reading it until NavRail is extracted too.
    readonly onIngestChanged: (files: readonly IngestFile[]) => void;
    // A source LANDED (fresh OR reused) — the shell grounds it into the active
    // conversation; `id` names the upload it settles (resolves that upload's
    // chip). Optional — the integrator wires it; ingest works without it.
    readonly onSourceAdded?: (source: AddedSource, id: IngestId) => void;
    // An upload FAILED — a send waiting on it goes out without it.
    readonly onSourceFailed?: (id: IngestId) => void;
    // The shell's ONE stamped trigger door (determinism M9.2). Every ingest
    // signal this host raises — a user drag/drop/pick, a per-file port
    // resolution, the batch coda — is fired through here with its provenance
    // (`user` for gestures, `port` for `addSource` re-entries) instead of
    // calling the local decider bridge directly; the door routes it back into
    // `applyCommand` above. Provided by AppShellHost.
    readonly applyIngest: (
        provenance: TriggerProvenance,
        command: IngestCommand,
    ) => void;
    // The conversation active at drop/pick time — threaded so the shell can
    // associate the just-minted IngestId(s) with it (F83 linkage). `null` when
    // no conversation owns the moment (a resource/settings tab, or none): the
    // drop still ingests, but no composer chip is claimed (rail-only).
    readonly activeConversationId: string | null;
    // Report the ids just minted for a drop/pick + the conversation they belong
    // to, so the shell records the F83 linkage the composer projection reads.
    // Called with EVERY minted id (accepted, rejected alike); the pure
    // projection filters to what earns a chip.
    readonly onFilesQueued: (
        ids: readonly IngestId[],
        conversationId: string | null,
    ) => void;
};

export const IngestHost = forwardRef<IngestHostHandle, IngestHostProps>(
    (
        {
            ports,
            workspaceName,
            onLibraryChanged,
            openRef,
            showNotice,
            onIngestChanged,
            onSourceAdded,
            onSourceFailed,
            applyIngest,
            activeConversationId,
            onFilesQueued,
        },
        ref,
    ) => {
        const { t } = useLingui();
        // Localize a typed failure into the one-line string the failure
        // toast consumes — same shape as AppShellHost's own `displayText`.
        const displayText = (e: ExternalError): string => {
            const d = toDisplay(e);
            return d.body === undefined
                ? t(d.title)
                : `${t(d.title)} ${t(d.body)}`;
        };

        // Source-ingest state (owner decision B5): the running batch's
        // per-file progress AND the drag-hover arm, now a single value owned by
        // the pure `ingestDecider` (determinism M9.1). The batch list, the drop
        // indication and the enter/leave drag depth were three separate
        // useState/useRef cells written directly from the async loop and the
        // window drag listeners; they are one folded `IngestState` now.
        const [ingestState, setIngestState] = useState(initialIngestState);
        const fileInputRef = useRef<HTMLInputElement | null>(null);
        // The disjoint-id mint (impure shell edge): each batch gets a contiguous
        // IngestId range that never overlaps a concurrent batch's, so a settle
        // only ever matches its own batch's pending entry (the guard the fold
        // now expresses structurally).
        const ingestSeq = useRef(0);
        // The soft-cancel token (ADR-I5, determinism M9): a shell-held ref the
        // running batch loop reads BETWEEN files. The pure decider never touches
        // it — a `cancel_batch` command yields a `signal_cancel` effect that
        // flips it here; each new run resets it (see `run` below). Coarse on
        // purpose (one token for all in-flight batches): with the typical single
        // batch it IS "the batch"; concurrent-batch cancel is a known MVP
        // simplification, matching the owner's SOFT-cancel decision.
        const cancelRef = useRef(false);

        useEffect(() => {
            onIngestChanged(ingestState.batch);
        }, [ingestState.batch, onIngestChanged]);

        // === Source ingest (owner decision B5) ===============================
        // The impure edges of the ingest flow — the hidden file picker, the
        // window drag-drop listeners, the FileReader and the `addSource`
        // round-trips — now feed ONE decider choke point. Progress surfaces as
        // the QUIET status line under the rail's Sources section (deliberately
        // not toasts: a toast per file would interrupt N times for a process
        // the author just started, and the line sits where the new rows land);
        // failures keep the danger-toast idiom (a `notify` effect).

        // Mint a disjoint IngestId range for a batch and pair each id with its
        // filename + raw `File` handle. The impure edge (the `ingestSeq` ref);
        // the decider only routes the results, never reads the bytes.
        const mintBatch = (files: readonly File[]): readonly QueuedFile[] => {
            const startId = ingestSeq.current;
            ingestSeq.current += files.length;
            return files.map((f, i) => ({
                id: startId + i,
                filename: f.name,
                // Read the byte size here (impure edge) so the pure decider can
                // size-gate (Win 3) from a plain value, never the `File`.
                size: f.size,
                file: f,
            }));
        };

        // The ONE seam between the impure shell and the pure ingest decider
        // (ingestDecider.ts): decide against the current `ingestState`, fold the
        // resulting events onto the LATEST state (functional update — never
        // clobbers a concurrent batch), then interpret the effects here. A
        // refusal (`settle_after_clear` — a settle for an id the user already
        // cleared) is swallowed user-silently, visible only in dev.
        //
        // Determinism M9.2: this is now the ROUTE TARGET, not called directly.
        // Every ingest signal fires through the shell's one stamped door
        // (`applyIngest` prop, provenance-tagged); the door routes it back here
        // via the imperative handle's `applyCommand`. Same decide→evolve→
        // interpret work, same timing (the door hop is synchronous) — only the
        // path in changed, so every trigger now passes the stamped door.
        const applyIngestCommand = (command: IngestCommand) => {
            const decision = decideIngest(ingestState, command);
            if (Result.isErr(decision)) {
                if (import.meta.env.DEV)
                    console.debug(
                        "[ingest] refused",
                        decision.error.type,
                        command.type,
                    );
                return;
            }
            setIngestState((prev) =>
                evolveIngestAll(prev, decision.value.events),
            );
            for (const effect of decision.value.effects)
                runIngestEffect(effect);
        };

        // The imperative handle: `open()` for the many "add source" affordances
        // AppShellHost owns, and `applyCommand` for the stamped door to route a
        // trigger back into the decider bridge above. No deps array — the handle
        // recreates each render so `applyCommand` always closes over the LATEST
        // `applyIngestCommand` (hence the current `ingestState`).
        useImperativeHandle(
            ref,
            (): IngestHostHandle => ({
                open: () => fileInputRef.current?.click(),
                applyCommand: applyIngestCommand,
            }),
        );

        // The impurity home: minting is already done (ids ride the command),
        // so this reads files, calls ports, opens the tab and toasts. Each file
        // resolution and the batch coda re-enter the decider as commands.
        const runIngestEffect = (effect: IngestEffect) => {
            switch (effect.type) {
                case "run_ingest_batch": {
                    const items = effect.items;
                    // Each `addSource` resolution re-enters the decider through
                    // the stamped door with `port` provenance (determinism
                    // M9.2) — the world outcome, not a user gesture.
                    const fromPort: TriggerProvenance = {
                        type: "port",
                        port: "addSource",
                    };
                    const run = async () => {
                        // A fresh batch starts uncancelled: a NEW drop/pick (or a
                        // retry, which also emits this effect) after a prior
                        // cancel ingests normally.
                        cancelRef.current = false;
                        // SEQUENTIAL on purpose: server-side text extraction is
                        // the bottleneck, so parallel uploads finish no sooner —
                        // they only contend for it, and one-by-one keeps the
                        // progress order honest.
                        // Ids a soft-cancel skipped (never started) — dropped by
                        // the coda so they don't linger as ghost pending rows.
                        const discardedIds: IngestId[] = [];
                        for (const [k, { id, file }] of items.entries()) {
                            // SOFT cancel (ADR-I5): checked BETWEEN files. The
                            // just-awaited file already settled (its file_landed/
                            // file_failed ran below), so it is KEPT; the
                            // remaining, never-started ids are discarded.
                            if (cancelRef.current) {
                                for (const rest of items.slice(k))
                                    discardedIds.push(rest.id);
                                break;
                            }
                            const r = await ports.addSource({ file });
                            Result.match<AddedSource, ExternalError, void>({
                                ok: (added) => {
                                    // Report the landing — fresh or reused
                                    // alike (both are "the source that's now
                                    // in the workspace").
                                    onSourceAdded?.(added, id);
                                    applyIngest(fromPort, {
                                        type: "file_landed",
                                        id,
                                        source: added,
                                    });
                                },
                                err: (e) => {
                                    onSourceFailed?.(id);
                                    applyIngest(fromPort, {
                                        type: "file_failed",
                                        id,
                                        filename: file.name,
                                        error: e,
                                        // Localized shell-side (the decider owns
                                        // no i18n instance); several failures in
                                        // one batch let the LAST toast win — an
                                        // accepted MVP cut.
                                        message: displayText(e),
                                        // The raw handle, RETAINED on the failed
                                        // state so retry re-runs WITHOUT
                                        // re-picking (ADR-I5). Byte-free in the
                                        // recorded essence (intentDispatch drops
                                        // it).
                                        file,
                                    });
                                },
                            })(r);
                        }
                        // Batch over: reload the rail once. Calm ingest (F-B /
                        // DB3) — the coda no longer opens a tab; the composer's
                        // attachment chip is the confirmation. `discardedIds` is
                        // non-empty only when a soft-cancel broke the loop early.
                        applyIngest(fromPort, {
                            type: "batch_settled",
                            discardedIds,
                        });
                    };
                    void run();
                    return;
                }
                case "reload_library": {
                    // ADR-I4: concurrent batches both reload the SHARED `library`
                    // cell (AppShellHost Group P) — last-writer-wins there is the
                    // deferred client-event-fold substrate's root fix, NOT this
                    // decider's. Routing the reload through an effect lets a later
                    // serializer land without touching the decider.
                    const run = async () => {
                        const lib = await ports.loadLibrary();
                        if (Result.isOk(lib)) onLibraryChanged(lib.value);
                    };
                    void run();
                    return;
                }
                case "open_ref":
                    openRef(effect.ref);
                    return;
                case "notify":
                    showNotice(effect.notice);
                    return;
                case "signal_cancel":
                    // ADR-I5: flip the shell-held cancellation token. The
                    // in-flight batch loop reads it between files and stops
                    // scheduling the rest. Pure ref write — no state; the decider
                    // already recorded the intent (`batch_cancelled`).
                    cancelRef.current = true;
                    return;
                default:
                    return exhaustSwitchCase(effect);
            }
        };

        // Full-window drag-drop ingest: dropping files ANYWHERE adds them as
        // sources to the workspace (owner decision B5 — accepted
        // everywhere, settings included). Only OS file drags arm the
        // overlay (`isFileDrag`); in-page drags pass untouched. No
        // dependency array, like AppShellHost's appCommand listener:
        // re-subscribing every render is cheap and the handlers are never
        // stale (drag depth survives in its ref).
        useEffect(() => {
            const fileDrag = (e: DragEvent): boolean =>
                e.dataTransfer !== null &&
                isFileDrag([...e.dataTransfer.types]);
            const onDragEnter = (e: DragEvent) => {
                if (!fileDrag(e)) return;
                applyIngest({ type: "user" }, { type: "drag_entered" });
            };
            const onDragOver = (e: DragEvent) => {
                if (!fileDrag(e)) return;
                // Required to mark the window as a valid drop target (pure DOM,
                // no state — stays a shell concern, not a command).
                e.preventDefault();
            };
            const onDragLeave = (e: DragEvent) => {
                if (!fileDrag(e)) return;
                applyIngest({ type: "user" }, { type: "drag_departed" });
            };
            const onDrop = (e: DragEvent) => {
                if (!fileDrag(e)) return;
                // Without this the browser navigates to the dropped file.
                e.preventDefault();
                // The decider resets the drag arm AND queues the batch (an
                // empty drop just resets).
                const items = mintBatch(
                    e.dataTransfer === null ? [] : [...e.dataTransfer.files],
                );
                // F83: record the drop-time conversation for each minted id
                // BEFORE dispatching, so the optimistic chip resolves to the
                // right composer the instant `files_queued` folds.
                onFilesQueued(
                    items.map((it) => it.id),
                    activeConversationId,
                );
                applyIngest({ type: "user" }, { type: "dropped_files", items });
            };
            window.addEventListener("dragenter", onDragEnter);
            window.addEventListener("dragover", onDragOver);
            window.addEventListener("dragleave", onDragLeave);
            window.addEventListener("drop", onDrop);
            return () => {
                window.removeEventListener("dragenter", onDragEnter);
                window.removeEventListener("dragover", onDragOver);
                window.removeEventListener("dragleave", onDragLeave);
                window.removeEventListener("drop", onDrop);
            };
        });

        return (
            <>
                {/* The ingest picker — hidden; the rail's "+", the File
                    menu's add-source command and WorkspaceHome's next step
                    all click it (via the imperative handle). `value` is
                    reset after each pick so re-choosing the same file fires
                    change again. */}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPTED_SOURCE_EXTENSIONS.join(",")}
                    style={{ display: "none" }}
                    onChange={(e) => {
                        const files = e.currentTarget.files;
                        const items = mintBatch(
                            files === null ? [] : [...files],
                        );
                        // F83: same linkage as the drop path (see onDrop).
                        onFilesQueued(
                            items.map((it) => it.id),
                            activeConversationId,
                        );
                        applyIngest(
                            { type: "user" },
                            { type: "picked_files", items },
                        );
                        e.currentTarget.value = "";
                    }}
                />
                {ingestState.dropIndication === "over" ? (
                    <SourceDropOverlay workspaceName={workspaceName} />
                ) : null}
            </>
        );
    },
);
IngestHost.displayName = "IngestHost";
